import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggerService } from './utils/logger.service';
import { ToastController } from '@ionic/angular/standalone';
import { HardwareService } from './hardware.service';
import { IonicColors } from '../../shared/enums/ionicColors.enum';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class NetworkStatusService {// Usiamo un BehaviorSubject per avere sempre l'ultimo valore disponibile
    private isOnlineSubject = new BehaviorSubject<boolean>(Capacitor.isNativePlatform() ? true : (typeof navigator !== 'undefined' ? navigator.onLine : true));
    // Observable che emette true se online, false se offline
    public isOnline$: Observable<boolean> = this.isOnlineSubject.asObservable();
    // Dichiarazione per memorizzare l'istanza del toast attualmente mostrato
    private activeToast: HTMLIonToastElement | null = null;
    // Variabile per evitare conflitti se lo stato cambia velocemente
    private isToastOperationInProgress = false;
    private listenerInitialized = false;
    

    constructor(
        private loggerService: LoggerService,
        private toastController: ToastController,
        private hardwareService: HardwareService,
        private ngZone: NgZone
    ) {
    }
    
    public async initNetworkListener() {
        if (this.listenerInitialized) {
            return;
        }

        this.listenerInitialized = true;
        if (!Capacitor.isNativePlatform()) {
            const update = () => this.ngZone.run(() => this.updateStatus(navigator.onLine));
            window.addEventListener('online', update);
            window.addEventListener('offline', update);
            update();
            return;
        }
        // 1. Leggi lo stato iniziale
        try {
            const status = await Network.getStatus();
            this.ngZone.run(() => this.updateStatus(status.connected));
            // 2. Ascolta i cambiamenti NATIVI
            await Network.addListener('networkStatusChange', status => {
                // Gli eventi nativi girano fuori da Angular.
                this.ngZone.run(() => this.updateStatus(status.connected));
            });
        } catch {
            this.listenerInitialized = false;
            this.loggerService.warn('NETWORK_LISTENER_UNAVAILABLE');
        }
    }

    // Metodo centralizzato per gestire il cambio di stato
    private updateStatus(isOnline: boolean) {
        // Evita di ripetere l'azione se lo stato non è cambiato davvero
        if (this.isOnlineSubject.value === isOnline) {
        return;
        }

        this.isOnlineSubject.next(isOnline);
        
        this.loggerService.info((this.hardwareService.isMobile() ? 'SMARTPHONE ' : 'COMPUTER ') + ` ${isOnline ? 'ONLINE' : 'OFFLINE'}`);

        if (isOnline) {
        this.dismissOfflineToast();
        } else {
        this.presentOfflineToast();
        }
    }

    // Funzione per creare e mostrare il toast
    private async presentOfflineToast() {
        // Se stiamo già lavorando su un toast o ce n'è uno attivo, fermati
        if (this.activeToast || this.isToastOperationInProgress) {
        return;
        }

        this.isToastOperationInProgress = true;

        try {
        const toast = await this.toastController.create({
            message: 'ATTENZIONE! ' + (this.hardwareService.isMobile() ? 'Smartphone' : 'Computer') + ' non connesso alla rete',
            position: 'bottom',
            color: IonicColors.RED,
            mode: 'ios',
            duration: 0, // 0 = non si chiude mai da solo (importante)
            cssClass: 'network-toast' // Opzionale: per stili extra
        });

        this.activeToast = toast;
        await toast.present();
        } catch (e) {
        console.error('Errore presentazione toast', e);
        } finally {
        this.isToastOperationInProgress = false;
        }
    }

    // Funzione per chiudere il toast
    private async dismissOfflineToast() {
        // Se non c'è toast o se ne sta aprendo uno proprio ora, aspetta o ignora
        if (!this.activeToast) {
        return;
        }

        // Se c'è un'operazione in corso, riprova tra poco (piccolo debounce per race conditions)
        if (this.isToastOperationInProgress) {
            setTimeout(() => this.dismissOfflineToast(), 200);
            return;
        }

        try {
        await this.activeToast.dismiss();
        this.activeToast = null;
        } catch (e) {
        // A volte dismiss fallisce se il toast è già in chiusura, ignoriamo l'errore
        this.activeToast = null;
        }
    }
}
